import { getEnumFromValue } from '@NGUtils/functions';
import { useUserPersonalData } from '@apis/queries/useUserPersonalData/useUserPersonalData';
import { isSocketDebugNotificationsActivated } from '@functions/functions';
import { EToastVariant } from '@notifications/components/Toast/Toast.enum';
import { IGetUserPlatformsDataResponse } from '@services/users/users.services.type';
import useAddToast from '@store/toastsStore/hooks/useAddToast';
import { EGithubAppState } from '@store/userStore/userStore.enum';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect } from 'react';
import { userKeys } from 'src/apis/queries/queriesKeys';
import { SUCCESS } from 'src/texts/feedbackMessages';
import { EGithubEvent, ETopic } from '../socketStore.enum';
import { ISocketMessage } from '../socketStore.type';
import { useSocket } from './useSocket';

export const useGithubSocketEvents = () => {
	const [socket] = useSocket();
	const { addToast } = useAddToast();
	const { userId } = useUserPersonalData();

	const queryClient = useQueryClient();

	const updateUserDataState = useCallback(
		(username: string, newGithubAppState?: EGithubAppState) => {
			queryClient.setQueryData(userKeys.platforms(userId), (prevUserPlatformsData?: IGetUserPlatformsDataResponse) => {
				// if the updater function receives undefined as input, we return undefined to bail out of the update
				if (!prevUserPlatformsData) return undefined;

				return {
					...prevUserPlatformsData,
					github: username,
					githubAppState: newGithubAppState ?? prevUserPlatformsData.githubAppState
				};
			});
		},
		[userId, queryClient]
	);

	const handleMessage = useCallback(
		(message: ISocketMessage<ETopic.github, EGithubEvent>) => {
			if (message.topic !== ETopic.github) return;

			try {
				switch (message.event) {
					// tested
					case EGithubEvent.appInstalled: {
						const typedMessage = message as ISocketMessage<ETopic.github, EGithubEvent.appInstalled>;

						const status = typedMessage.data.status;
						const _githubAppState = getEnumFromValue(status, EGithubAppState);

						if (_githubAppState !== EGithubAppState.unset) {
							updateUserDataState(typedMessage.data.githubUsername, _githubAppState);
						}

						if (isSocketDebugNotificationsActivated()) {
							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.github.appInstalled,
								variant: EToastVariant.success
							});
						}
						break;
					}
					case EGithubEvent.appRemoved: {
						const typedMessage = message as ISocketMessage<ETopic.github, EGithubEvent.appRemoved>;

						const status = typedMessage.data.status;
						const _githubAppState = getEnumFromValue(status, EGithubAppState);

						if (_githubAppState !== EGithubAppState.unset) {
							updateUserDataState(typedMessage.data.githubUsername, _githubAppState);
						}

						if (isSocketDebugNotificationsActivated()) {
							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.github.appRemoved,
								variant: EToastVariant.success
							});
						}
						break;
					}
					// remove the - from the event name (the - was added due to event name duplication)
					case EGithubEvent.usernameUpdated.replace('-', ''): {
						const typedMessage = message as ISocketMessage<ETopic.github, EGithubEvent.usernameUpdated>;

						updateUserDataState(typedMessage.data.githubUsername);

						if (isSocketDebugNotificationsActivated()) {
							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.github.usernameUpdated(message.data.githubUsername),
								variant: EToastVariant.success
							});
						}
						break;
					}
					case EGithubEvent.repositoryChanged:
						const typedMessage = message as ISocketMessage<ETopic.github, EGithubEvent.repositoryChanged>;

						const status = typedMessage.data.status;
						const _githubAppState = getEnumFromValue(status, EGithubAppState);

						if (_githubAppState !== EGithubAppState.unset) {
							updateUserDataState(typedMessage.data.githubUsername, _githubAppState);
						}

						if (isSocketDebugNotificationsActivated()) {
							addToast({
								uuid: message.notificationId,
								title: SUCCESS.generic,
								message: SUCCESS.github.repositoryChanged(typedMessage.data.status),
								variant: EToastVariant.success
							});
						}
					default:
						break;
				}
			} catch (err) {
				console.error('useGithubSocketEvents', err);
			}
		},
		[addToast, updateUserDataState]
	);

	useEffect(() => {
		if (socket) {
			// fired when socket is successfully connected
			socket.on('connect', () => {
				console.debug(ETopic.github, 'socket connected');
			});

			socket.on(ETopic.github, handleMessage);

			return () => {
				socket.off('connect');
				socket.off(ETopic.github, handleMessage);
			};
		}
	}, [handleMessage, socket]);
};
